import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { groupBy } from "lodash";
import { JSX, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BackArrowIcon from "../../../assets/arrow-back.svg?react";
import LeftArrowIcon from "../../../assets/arrow-left.svg?react";
import CloseIcon from "../../../assets/close.svg?react";
import { RightPanelTabs } from "../../../constants/Panels";
import { AppContext, OverettContext } from "../../../Context";
import { changeRightPanelTab, setRightPanelActiveTab } from "../../../store/appSlice";
import {
    decreaseOverettCreationStep,
    setIsCreatingOverett,
    setIsEditingOverett,
    setIsOverettChartOpen,
    setOverettCreationStep,
} from "../../../store/overettSlice";
import { StoreState } from "../../../store/rootReducer";
import { applyEdits, getMapView, highlightFeature, removedAndDiscontinuedDefExpr } from "../../../utils/arcgisUtils";
import { unhideLighthouses } from "../../../utils/lighthouseUtils";
import usePrevious from "../../../utils/usePrevious";
import NewOverettPanel from "./NewOverettPanel";
import "./OverettEditor.css";
import OverettEditorContent, { OverettCreationStep } from "./OverettEditorContent";
import OverettLinePanel from "./OverettLinePanel";
import { AppDispatch } from "../../../store/storeSetup";

const OverettEditor = () => {
    const dispatch = useDispatch<AppDispatch>();

    const appContext = useContext(AppContext);
    const overettContext = useContext(OverettContext);
    const overettObjects = overettContext?.overettObjects.value;
    const overettLinjeRef = overettContext?.overettLinjeRef.value;
    const editorVMUpperOverett = overettContext?.editorVMUpperOverett.value;
    const editorVMLowerOverett = overettContext?.editorVMLowerOverett.value;
    const selectedFeature = appContext?.selectedFeature.value;

    const overettCreationStep = useSelector((state: StoreState) => state.overett.overettCreationStep);
    const isCreatingOverett = useSelector((state: StoreState) => state.overett.isCreatingOverett);
    const isEditingOverett = useSelector((state: StoreState) => state.overett.isEditingOverett);
    const showRemovedFeatures = useSelector((state: StoreState) => state.app.showRemovedFeatures);

    const rightPanelActiveTab = useSelector((state: StoreState) => state.app.rightPanelActiveTab);

    const overettHighlightsRef = overettContext?.overettHighlightsRef;

    const [menuItem, setMenuItem] = useState<JSX.Element | null>(null);
    const [featuresBeforeEdit, setFeaturesBeforeEdit] = useState<Graphic[]>([]);

    const exitOverettEditor = () => {
        // Clear highlights first to ensure they're removed
        overettHighlightsRef?.clear();

        overettContext?.overettObjects.set({ upper: null, lower: null });
        overettContext!.overettLinjeRef.value.current = null;

        if (editorVMUpperOverett?.current) {
            editorVMUpperOverett.current?.activeWorkflow?.cancel();
            editorVMUpperOverett.current?.destroy();
            editorVMUpperOverett.current = undefined;
        }

        if (editorVMLowerOverett?.current) {
            editorVMLowerOverett.current?.activeWorkflow?.cancel();
            editorVMLowerOverett.current?.destroy();
            editorVMLowerOverett.current = undefined;
        }

        const mapView = getMapView();
        const overlayGraphicsLayer = mapView?.map?.findLayerById("overettOverlayLayer") as GraphicsLayer;
        if (overlayGraphicsLayer) overlayGraphicsLayer.removeAll();

        const overettLinjeLysLayer = mapView?.map?.findLayerById("Overettlinje lys") as FeatureLayer;
        if (overettLinjeLysLayer) {
            overettLinjeLysLayer.definitionExpression = removedAndDiscontinuedDefExpr(
                overettLinjeLysLayer,
                showRemovedFeatures
            );
            overettLinjeLysLayer.visible = true;
        }

        const overettLinjeMerkeLayer = mapView?.map?.findLayerById("Overettlinje merke") as FeatureLayer;
        if (overettLinjeMerkeLayer) {
            overettLinjeMerkeLayer.definitionExpression = removedAndDiscontinuedDefExpr(
                overettLinjeMerkeLayer,
                showRemovedFeatures
            );
            overettLinjeMerkeLayer.visible = true;
        }

        const lighthouseEditingLayer = mapView?.map?.findLayerById("FyrlyktEditing") as GraphicsLayer;
        if (lighthouseEditingLayer) {
            lighthouseEditingLayer.graphics.removeAll();
        }

        const lighthouseEditingLayer2 = mapView?.map?.findLayerById("FyrlyktEditing2") as GraphicsLayer;
        if (lighthouseEditingLayer2) {
            lighthouseEditingLayer2.graphics.removeAll();
        }
        unhideLighthouses(true);

        dispatch(setOverettCreationStep(null));
        dispatch(setIsCreatingOverett(false));
        dispatch(setIsEditingOverett(false));
        dispatch(setIsOverettChartOpen(false));
        if (selectedFeature) {
            dispatch(setRightPanelActiveTab(RightPanelTabs.EDITOR));
        } else {
            dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
        }
    };

    const highlightOverettFeatures = async () => {
        if (!overettObjects) return;

        // Clear existing highlights first
        overettHighlightsRef?.clear();

        const handles = [];
        const features = [overettObjects.upper, overettObjects.lower];
        if (overettLinjeRef?.current) {
            features.push(overettLinjeRef.current);
        }

        for (const feature of features) {
            if (!feature) continue;
            const handle = await highlightFeature(feature, "overett");
            if (!handle) continue; // Changed from return to continue to process other features
            handles.push(handle);
        }

        // Only store handles if we have any
        if (handles.length > 0) {
            overettHighlightsRef?.store(handles, false); // Don't clear again since we already cleared above
        }
    };

    const clearLineGraphics = () => {
        const mapView = getMapView();
        if (!mapView?.map) return;

        const overettLinjeLayer = mapView.map.findLayerById("OverettlinjeEditing") as GraphicsLayer;
        if (!overettLinjeLayer) return;

        overettLinjeLayer.removeAll();
    };

    const cancelMovement = async () => {
        const mapView = getMapView();

        const featuresBeforeEditGrouped = groupBy(featuresBeforeEdit, (feat) => feat.layer?.id);
        for (const [layerId, features] of Object.entries(featuresBeforeEditGrouped)) {
            const layer = mapView?.map?.findLayerById(layerId) as FeatureLayer;
            await applyEdits(layer, {
                updateFeatures: features,
            });
        }
        setFeaturesBeforeEdit([]);
    };

    const goToEditing = () => {
        const result = dispatch(changeRightPanelTab(RightPanelTabs.EDITOR));
        if (!result) {
            // User cancelled, don't unselect
            return;
        }
        cancelMovement();
    };

    useEffect(() => {
        highlightOverettFeatures();

        // Cleanup function to clear highlights when overettObjects change
        return () => {
            overettHighlightsRef?.clear();
        };
    }, [overettObjects]);

    useEffect(() => {
        if (!overettLinjeRef?.current) return;

        highlightFeature(overettLinjeRef.current, "overett").then((handle) => {
            if (!handle) return;
            overettHighlightsRef?.store([handle], false);
        });

        // Cleanup function to clear highlights when overettLinjeRef changes
        return () => {
            overettHighlightsRef?.clear();
        };
    }, [overettLinjeRef]);

    useEffect(() => {
        if (overettLinjeRef?.current) {
            dispatch(setOverettCreationStep(OverettCreationStep.EditOverett));
            dispatch(setIsCreatingOverett(false));
            dispatch(setIsEditingOverett(true));
        } else if (overettObjects?.lower && overettObjects?.upper) {
            dispatch(setOverettCreationStep(OverettCreationStep.DrawLine));
        } else if (
            (overettObjects?.lower && !overettObjects?.upper) ||
            (!overettObjects?.lower && overettObjects?.upper)
        ) {
            dispatch(setOverettCreationStep(OverettCreationStep.SelectObjects));

            // Clear existing highlights before adding new one
            overettHighlightsRef?.clear();

            if (overettObjects?.upper) {
                highlightFeature(overettObjects.upper, "overett").then((handle) => {
                    if (!handle) return;
                    overettHighlightsRef?.store([handle], false);
                });
            }
            dispatch(setIsCreatingOverett(true));
            dispatch(setIsEditingOverett(false));
        }
    }, [overettObjects, overettLinjeRef?.current]);

    useEffect(() => {
        switch (overettCreationStep) {
            case OverettCreationStep.SelectObjects:
                setMenuItem(<NewOverettPanel />);
                clearLineGraphics();
                break;
            case OverettCreationStep.DrawLine:
                setMenuItem(<OverettLinePanel />);
                break;
            case OverettCreationStep.EditOverett:
                setMenuItem(
                    <OverettEditorContent
                        exitOverettEditor={exitOverettEditor}
                        highlightOverettFeatures={highlightOverettFeatures}
                        setFeaturesBeforeEdit={setFeaturesBeforeEdit}
                    />
                );
                break;
            default:
                setMenuItem(null);
                break;
        }
    }, [overettCreationStep]);

    const previousRightPanelActiveTab = usePrevious(rightPanelActiveTab);

    useEffect(() => {
        if (previousRightPanelActiveTab === RightPanelTabs.OVERETT && rightPanelActiveTab !== RightPanelTabs.OVERETT) {
            exitOverettEditor();
        }
    }, [rightPanelActiveTab]);

    // Cleanup effect when component unmounts
    useEffect(() => {
        return () => {
            // Ensure highlights are cleared when component unmounts
            overettHighlightsRef?.clear();
        };
    }, []);

    if (!isCreatingOverett && !isEditingOverett) return null;

    return (
        <div className="feature-editor-container">
            <button type="button" className={`feature-viewer-collapse-button overett-editor-collapse-button`}>
                <LeftArrowIcon />
            </button>
            <div className={`feature-viewer overett-editor`}>
                <div className="feature-viewer-header editor-close">
                    {overettCreationStep === OverettCreationStep.DrawLine && (
                        <button
                            type="button"
                            className="empty-button center"
                            onClick={() => dispatch(decreaseOverettCreationStep())}
                        >
                            <BackArrowIcon />
                            Avbryt tegning
                        </button>
                    )}
                    {selectedFeature && overettCreationStep !== OverettCreationStep.DrawLine && (
                        <button onClick={goToEditing} className="empty-button flex-row center">
                            <BackArrowIcon />
                            Tilbake
                        </button>
                    )}
                    <button
                        type="button"
                        className="empty-button"
                        onClick={() => {
                            cancelMovement();
                            exitOverettEditor();
                        }}
                    >
                        <CloseIcon />
                    </button>
                </div>
                {menuItem}
            </div>
        </div>
    );
};

export default OverettEditor;
